/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningDealHistory.module.scss';

const DH_POINTS = [
  'Every event is tied to the filing that evidences it',
  'Nothing appears on the list because a blog said so',
  'The record covers the group, not just the target',
  'An acquisition by a parent two jurisdictions away sits on the same timeline',
];

type TxType = 'acq' | 'ctl' | 'dis' | 'fin' | 'inc';

type Transaction = {
  date: string;
  type: TxType;
  label: string;
  detail: string;
  meta: string;
  value: string;
  nd?: boolean;
};

const NEW_TX: Transaction = {
  date: 'Feb 2026',
  type: 'acq',
  label: 'Acquisition',
  detail: 'Lindqvist Verktyg AB',
  meta: 'Kestrel Precision Ltd · Bolagsverket 2026-02-11',
  value: 'n/d',
  nd: true,
};

const TRANSACTIONS: Transaction[] = [
  {
    date: 'Nov 2025',
    type: 'acq',
    label: 'Acquisition',
    detail: 'Halden Tooling AS',
    meta: 'Kestrel Precision Ltd · Brønnøysund 2025-11-18',
    value: 'n/d',
    nd: true,
  },
  {
    date: 'Mar 2024',
    type: 'ctl',
    label: 'Change of control',
    detail: 'Brekke Kapital AS',
    meta: 'Kestrel Holdings · Conf. stmt 2024-03-29',
    value: '62.4%',
  },
  {
    date: 'Aug 2022',
    type: 'acq',
    label: 'Acquisition',
    detail: 'Meridian Fixtures Ltd',
    meta: 'Kestrel Precision Ltd · Companies House SH01',
    value: '100%',
  },
  {
    date: 'Jan 2021',
    type: 'dis',
    label: 'Disposal',
    detail: 'Kestrel Coatings division',
    meta: 'Kestrel Holdings · Accounts FY2021, note 7',
    value: 'n/d',
    nd: true,
  },
  {
    date: 'Jun 2018',
    type: 'fin',
    label: 'Financing',
    detail: 'secured growth facility',
    meta: 'Kestrel Precision Ltd · Companies House MR01',
    value: '£6.0M',
  },
  {
    date: 'Feb 2016',
    type: 'inc',
    label: 'Incorporation',
    detail: 'holding structure',
    meta: 'Kestrel Holdings · Companies House IN01',
    value: '—',
    nd: true,
  },
];

const LEGEND: { type: TxType; label: string }[] = [
  { type: 'acq', label: 'Acquisition' },
  { type: 'ctl', label: 'Control' },
  { type: 'dis', label: 'Disposal' },
  { type: 'fin', label: 'Financing' },
];

type Phase = 'idle' | 'syncing' | 'in' | 'settled';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const InvestmentScreeningDealHistory = () => {
  const { t } = useInt();
  const cardRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const [phase, setPhase] = useState<Phase>('idle');
  const [count, setCount] = useState(TRANSACTIONS.length);
  const [synced, setSynced] = useState('synced 2 min ago');

  useEffect(() => {
    const node = cardRef.current;

    if (!node) {
      return undefined;
    }

    const clear = () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };

    const reset = () => {
      clear();
      setPhase('idle');
      setCount(TRANSACTIONS.length);
      setSynced('synced 2 min ago');
    };

    if (prefersReducedMotion()) {
      setPhase('settled');
      setCount(TRANSACTIONS.length + 1);
      setSynced('synced just now');
      return undefined;
    }

    const run = () => {
      reset();

      timeoutsRef.current.push(setTimeout(() => setPhase('syncing'), 1400));
      timeoutsRef.current.push(
        setTimeout(() => {
          setPhase('in');
          setCount(TRANSACTIONS.length + 1);
          setSynced('synced just now');
        }, 2450),
      );
      timeoutsRef.current.push(setTimeout(() => setPhase('settled'), 5200));
      timeoutsRef.current.push(
        setTimeout(() => {
          if (liveRef.current) {
            run();
          }
        }, 11500),
      );
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!liveRef.current) {
              liveRef.current = true;
              run();
            }
          } else {
            liveRef.current = false;
            reset();
          }
        });
      },
      { threshold: 0.35 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      clear();
    };
  }, []);

  return (
    <Section background="#EEF1FA" className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            align="left"
            title={t('The target has a past')}
            description={t(
              'Prior transactions tell you what the seller has done before, and who has already been round the table.',
            )}
          />

          <ul className={styles.list}>
            {DH_POINTS.map((point) => (
              <li key={point}>{t(point)}</li>
            ))}
          </ul>

          <div className={styles.actions}>
            <Button type="blue" size="lg" href="/ma-activity">
              {t('See M&A activity on the platform')}
            </Button>
          </div>
        </div>

        <div className={styles.card} ref={cardRef}>
          <div className={styles.cardBar}>
            <span className={styles.cardDot} />
            <span className={styles.cardName}>Transaction record · Kestrel Precision Holdings</span>
            <span className={styles.cardMeta}>{synced}</span>
          </div>

          <div className={cn(styles.sync, phase === 'syncing' && styles.syncRun)}>
            <i />
          </div>

          <div className={styles.cols}>
            <span>Date</span>
            <span>Transaction &amp; evidence</span>
            <span className={styles.colRight}>Size</span>
          </div>

          <div className={styles.txList}>
            <div className={cn(styles.slot, (phase === 'in' || phase === 'settled') && styles.slotIn)}>
              <div
                className={cn(
                  styles.tx,
                  styles[`tx${NEW_TX.type}` as 'txacq'],
                  styles.txHighlight,
                  phase === 'settled' && styles.txSettled,
                )}
              >
                <span className={styles.txDate}>{NEW_TX.date}</span>
                <span className={styles.txCopy}>
                  <span className={styles.txTitle}>
                    <em>{NEW_TX.label} ·</em> {NEW_TX.detail}
                    <span className={cn(styles.txNew, phase === 'settled' && styles.txNewOut)}>new</span>
                  </span>
                  <span className={styles.txMeta}>{NEW_TX.meta}</span>
                </span>
                <span className={cn(styles.txValue, NEW_TX.nd && styles.txValueNd)}>{NEW_TX.value}</span>
              </div>
            </div>

            {TRANSACTIONS.map((tx) => (
              <div key={tx.date} className={cn(styles.tx, styles[`tx${tx.type}` as 'txacq'])}>
                <span className={styles.txDate}>{tx.date}</span>
                <span className={styles.txCopy}>
                  <span className={styles.txTitle}>
                    <em>{tx.label} ·</em> {tx.detail}
                  </span>
                  <span className={styles.txMeta}>{tx.meta}</span>
                </span>
                <span className={cn(styles.txValue, tx.nd && styles.txValueNd)}>{tx.value}</span>
              </div>
            ))}
          </div>

          <div className={styles.cardFoot}>
            <span className={styles.txCount}>
              <b>{count}</b> transactions since 2016
            </span>
            <span className={styles.key}>
              {LEGEND.map((item) => (
                <span key={item.type} className={styles.keyItem}>
                  <i className={styles[`tx${item.type}` as 'txacq']} />
                  {item.label}
                </span>
              ))}
            </span>
          </div>
        </div>
      </div>
    </Section>
  );
};
